import { Empty, List, Modal, Rate, Spin } from 'antd'
import { isEmpty } from 'lodash-es'
import { FunctionComponent, ReactNode, useState } from 'react'
import { useToggle } from 'react-use'
import styled from 'styled-components'
import {
  useDeleteCollaborationRecordScoringMutation,
  useFetchCollaborationRecordScoringQuery,
} from '@/api/workspace-api/directory-api'
import { InitialLoadingBlock } from '@/components/common/initial-loading-block'
import Actions from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/actions'
import AddScoreModal from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/add-score-modal'
import EditScoreModal from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/edit-score-modal'
import { DataWrapper } from '@/components/kol/irm/common/data-wrapper'
import { TextWrapper } from '@/components/kol/irm/common/text-wrapper'
import { Time } from '@/components/kol/irm/common/time'
import { Wrapper } from '@/components/kol/irm/common/wrapper'
import { InfoWrapper } from '@/components/kol/irm/info-wrapper'
import { AvatarImage } from '@/components/workspace/avatar-image'
import { scoreValueScale } from '@/constants/irm/score-value-scale'
import { useAuth } from '@/hooks/use-auth'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Score } from '@/types/schema/directory/kol-management'

const { confirm } = Modal

interface CooperationFeedbackProps {
  kolId: string
}

const CooperationFeedback: FunctionComponent<CooperationFeedbackProps> = ({
  kolId,
}) => {
  const { formatMessage, formatDate } = useIntl()
  const [displayAddModal, setDisplayAddModal] = useToggle(false)
  const [displayEditModal, setDisplayEditModal] = useToggle(false)
  const [score, setScore] = useState<Score>()
  const { userInfo } = useAuth()
  const { data: scores, isFetching } = useFetchCollaborationRecordScoringQuery({
    kolId,
  })
  const [deleteScore] = useDeleteCollaborationRecordScoringMutation()

  const toggleAddModal: VoidFunction = () => {
    setDisplayAddModal(!displayAddModal)
  }

  const toggleEditModal: VoidFunction = () => {
    setDisplayEditModal(!displayEditModal)
  }

  const onDelete = (id: number): void => {
    confirm({
      title: formatMessage({ id: 'irm:update_deleted_cooperation_comment' }),
      content: formatMessage({
        id: 'irm:deleted_cooperation_comment_modal_content',
      }),
      okText: formatMessage({ id: 'general:btn_text_confirm' }),
      onOk() {
        deleteScore({ id })
      },
    })
  }

  const onEdit = (s: Score): void => {
    setScore(s)
    toggleEditModal()
  }

  return (
    <InfoWrapper
      title={formatMessage({ id: 'irm:title_collabration_comment' })}
      onClick={toggleAddModal}
    >
      {scores !== undefined ? (
        <Spin spinning={isFetching}>
          {isEmpty(scores) ? (
            <Empty
              description={formatMessage({
                id: 'autoreport:chart_label_empty_data',
              })}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={scores}
              itemLayout='horizontal'
              renderItem={(score: Score): ReactNode => {
                const name = score.User.Name
                return (
                  <List.Item>
                    <Wrapper>
                      <DataWrapper>
                        <AvatarImage name={name} size='default' />
                        <TextWrapper>
                          <div>
                            <Name>{name}</Name>
                            <TimeText>
                              {formatDate(score.UpdatedAt, 'dateTimeLong')}
                            </TimeText>
                          </div>
                          <Rate
                            allowHalf
                            disabled
                            style={{ margin: '8px 0' }}
                            value={score.ScoreValue / scoreValueScale}
                          />
                          <Description>{score.Comment}</Description>
                        </TextWrapper>
                      </DataWrapper>
                      <Actions
                        onDelete={(): void => onDelete(score.ID)}
                        onEdit={
                          score.UserID === Number(userInfo?.id)
                            ? (): void => onEdit(score)
                            : undefined
                        }
                      />
                    </Wrapper>
                  </List.Item>
                )
              }}
            />
          )}
        </Spin>
      ) : (
        <InitialLoadingBlock />
      )}
      <AddScoreModal
        kolId={kolId}
        open={displayAddModal}
        toggleModal={toggleAddModal}
      />
      <EditScoreModal
        open={displayEditModal}
        score={score}
        toggleModal={toggleEditModal}
      />
    </InfoWrapper>
  )
}

const Name = styled.span`
  font-size: 12px;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.unclassified.grey.grey1};
`

const TimeText = styled(Time)`
  margin: 0 0 0 8px;
`

const Description = styled.div`
  font-size: 14px;
  line-height: 140%;
  color: ${(props): string => props.theme.colors.base.grey.grey1};
  white-space: pre-line;
  max-width: 300px;
  overflow-wrap: break-word;
`

export default CooperationFeedback
